import * as React from "react";
import { useState, useRef, useEffect, lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  checkExisting,
  setActionType,
  setGeneratedNeedId,
  setIsChatModalOpen,
  setIsMsgModalOpen,
  setIsZoomStarted,
  setLink,
  setSearchPopUp,
} from "../../../../redux/slices/SearchBarSlice";
import {
  resetFormState,
  setAlternativePhoneOne,
  setDescription,
  setEmail,
  setPhone,
  setZipcode,
} from "../../../../redux/slices/RequestFormSlice";
import {
  setDropdownOpen,
  setIsModalOpen,
} from "../../../../redux/slices/BwvData";
import { userAction } from "../../../../redux/slices/userSlice";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { countryCodes } from "../../../CountryCodes";
import { toast } from "react-toastify";
import { sendMessage } from "../../Data";
import { useSocket } from "../../../../contexts/SocketContext";
import axiosClient from "../../../../axios-client";

// Lazy-load MUI components
const Button = lazy(() => import("@mui/material/Button"));
const Dialog = lazy(() => import("@mui/material/Dialog"));
const DialogActions = lazy(() => import("@mui/material/DialogActions"));
const DialogContent = lazy(() => import("@mui/material/DialogContent"));
const DialogTitle = lazy(() => import("@mui/material/DialogTitle"));

// Lazy-load utilities
const loadUtilities = async () => {
  const { default: axiosClient } = await import("../../../../axios-client");
  const { makeCall, postRequest, sendMsgMail, virtualTourClick } = await import(
    "../../Data"
  );
  const { GetZoomLink } = await import("../../Data2");
  return {
    axiosClient,
    makeCall,
    postRequest,
    sendMsgMail,
    virtualTourClick,
    GetZoomLink,
    toast,
  };
};

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Lazy-loaded AutoNeedForm component for step 1
const AutoNeedForm = lazy(() => import("./AutoNeedForm"));
// Lazy-loaded AutoNeedOtp component for step 2
const AutoNeedOtp = lazy(() => import("./AutoNeedOtp"));

const AutoNeedModal = () => {
  const islogin = useSelector((state) => state.user.isLogin);
  const userinfo = useSelector((state) => state.user.userInfo);
  const user = islogin ? JSON.parse(userinfo) : null;
  const {
    searchPopUp,
    sampleAd,
    actionType,
    checkExistingError,
    checkExistingStatus,
    vTName,
    vTNumber,
    answers,
  } = useSelector((state) => state.searchBar);
  const { sendNewMessage } = useSocket();
  const { dropdownOpen } = useSelector((state) => state.bwvData);
  const { searchQuery } = useSelector((state) => state.lcAds);
  const dispatch = useDispatch();
  const formData = useSelector((state) => state.requestForm);
  const [isLoading, setIsLoading] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const [eventDetailsError, setEventDetailsError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [secondaryPhoneError, setSecondaryPhoneError] = useState(false);
  const [zipcodeError, setZipcodeError] = useState(false);
  const [countryCodeError, setCountryCodeError] = useState(false);
  const [mandatoryQuestionsError, setMandatoryQuestionsError] = useState(false);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const { bwvSearchQuery } = useSelector((state) => state.bwvData);
  const [receivedOtp, setReceivedOtp] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const firstInputRef = useRef(null);
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("+1");
  const isAdmin = user && user.Role === "admin";
  const logUser = localStorage.getItem("USER_ROLE");
  const isVendor = logUser === "provider";

  // Reset states when dialog opens
  useEffect(() => {
    if (searchPopUp) {
      setRequestSubmitted(false);
      setStep(1);
      setOtp("");
      setReceivedOtp(null);
      setOtpError(false);
      setSelectedCountry("+1");
      setCountryCodeError(false);
      setMandatoryQuestionsError(false);
      dispatch(setPhone(""));
    }
    if (islogin) {
      setSelectedCountry(user?.CountryCode || "+1");
      dispatch(setPhone(user?.Phone || ""));
      dispatch(setAlternativePhoneOne(user?.secondaryPhone || ""));
      dispatch(setZipcode(user?.ZipCode || ""));
    }
  }, [searchPopUp]);

  const handleClose = () => {
    if (!requestSubmitted) {
      toast.warn("Please complete this form to proceed on the website.");
      return;
    }
    dispatch(setSearchPopUp(false));
    dispatch(setDescription(""));
    setDescriptionError(false);
    setEventDetailsError(false);
    setEmailError(false);
    setPhoneError(false);
    setSecondaryPhoneError(false);
    setZipcodeError(false);
    setCountryCodeError(false);
    setOtpError(false);
    setMandatoryQuestionsError(false);
    setStep(1);
  };

  const validateEventDetails = (eventDetails) => {
    if (!eventDetails) return false;
    try {
      const event = JSON.parse(eventDetails)[0];
      return (
        event.eventDate &&
        event.eventTime &&
        event.eventType &&
        event.eventLocation &&
        event.totalGuestCount &&
        event.eventDate.trim() !== "" &&
        event.eventTime.trim() !== "" &&
        event.eventType.trim() !== "" &&
        event.eventLocation.trim() !== "" &&
        event.totalGuestCount.trim() !== ""
      );
    } catch (error) {
      console.error("Error parsing eventDetails:", error);
      return false;
    }
  };

  const validateMandatoryQuestions = () => {
    if (formData.engagedCouple !== 1 || !sampleAd?.Questions) return true;
    try {
      const parsedQuestions = JSON.parse(sampleAd.Questions);
      return parsedQuestions.every((question) => {
        if (!question.mandatory) return true;
        const answer = answers[question.label];
        if (question.type === "text" && (!answer || answer.trim() === "")) {
          return false;
        }
        if (question.type === "options" && (!answer || answer === "")) {
          return false;
        }
        if (question.type === "check" && answer !== true) {
          return false;
        }
        return true;
      });
    } catch (error) {
      console.error(
        "Failed to parse sampleAd.Questions for validation:",
        error
      );
      return true;
    }
  };

  const validateForm = () => {
    let isValid = true;

    if (!islogin && !formData.Email) {
      setEmailError(true);
      toast.error("Email is required");
      isValid = false;
    }
    if (!formData.Zipcode || !/^\d{5}$/.test(formData.Zipcode)) {
      setZipcodeError(true);
      toast.error("Valid 5-digit zipcode is required");
      isValid = false;
    }
    if (!formData.Phone || formData.Phone.length !== 10) {
      setPhoneError(true);
      toast.error("Valid 10-digit phone number is required");
      isValid = false;
    }
    if (
      !formData.alternativePhoneOne ||
      formData.alternativePhoneOne.length !== 10
    ) {
      setSecondaryPhoneError(true);
      toast.error("Valid 10-digit secondary phone number is required");
      isValid = false;
    }
    if (!selectedCountry) {
      setCountryCodeError(true);
      toast.error("Please select a country code");
      isValid = false;
    }
    if (!formData?.Description || formData?.Description.trim() === "") {
      setDescriptionError(true);
      toast.error("Description is required");
      isValid = false;
    }
    if (formData.engagedCouple === 1) {
      const isEventDetailsValid = validateEventDetails(formData.eventDetails);
      if (!isEventDetailsValid) {
        setEventDetailsError(true);
        toast.error("Please fill in all event details");
        isValid = false;
      }
      const isQuestionsValid = validateMandatoryQuestions();
      if (islogin && !isQuestionsValid) {
        setMandatoryQuestionsError(true);
        toast.error("Please answer all mandatory questions");
        isValid = false;
      }
    }

    return isValid;
  };

  useEffect(() => {
    if (searchPopUp && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [searchPopUp, step]);

  const handleBackdropClick = (event) => {
    event.stopPropagation();
  };

  useEffect(() => {
    if (checkExistingStatus === "failed" && checkExistingError) {
      toast.error(checkExistingError);
    }
  }, [checkExistingStatus, checkExistingError]);

  const handlenavFromLink = () => {
    setHasNavigated(true);
    const newUrl = `/vendordetails/${sampleAd.Id}`;
    navigate(newUrl, { replace: true });
    setTimeout(() => {
      const postNavUrl = window.location.href;
      if (postNavUrl.includes("fbclid") || postNavUrl.includes("/home/")) {
        window.location.replace(
          `${process.env.REACT_APP_URL}/#/vendordetails/${sampleAd.Id}`
        );
      }
    }, 100);
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const handleStartZoomCall = async () => {
    const { toast, makeCall, GetZoomLink, sendMsgMail, virtualTourClick } =
      await loadUtilities();
    if (!islogin && !vTName) {
      toast.error("Please Enter Email");
      return;
    }
    if (!islogin && !vTNumber) {
      toast.error("Please Enter Phone Number");
      return;
    }
    const serviceInfo = JSON.parse(sampleAd?.additionalPeople || "[]");
    const newPerson1 = {
      email: !islogin ? vTName : user?.Email,
      phoneNumber: !islogin ? vTNumber : user?.Phone,
      countryCode: !islogin ? selectedCountry : user?.CountryCode,
    };
    let newPerson2;
    let newPerson3;
    if (sampleAd.profilePhone === sampleAd.Phone) {
      newPerson2 = {
        email: sampleAd?.Email,
        phoneNumber: sampleAd?.profilePhone,
        countryCode: sampleAd?.countryCode,
      };
    } else {
      newPerson2 = {
        email: sampleAd?.Email,
        phoneNumber: sampleAd?.profilePhone,
        countryCode: sampleAd?.countryCode,
      };
      newPerson3 = {
        email: sampleAd?.Email,
        phoneNumber: sampleAd?.Phone,
        countryCode: sampleAd?.countryCode,
      };
    }
    serviceInfo.unshift(newPerson1);
    serviceInfo.push(newPerson2);
    if (newPerson3) serviceInfo.push(newPerson3);
    const stringifiedAdditionalPeople = JSON.stringify(serviceInfo);
    let formData = {
      email: !islogin ? vTName : user?.Email,
      phoneNumber: !islogin ? vTNumber : user?.Phone,
      countryCode: !islogin ? selectedCountry : user?.CountryCode,
      phoneDetails: stringifiedAdditionalPeople,
      similarLocalVendor: sampleAd?.similarLocalVendor,
      customerName: `${user?.FirstName} ${user?.LastName}`,
      serviceId: sampleAd?.Id,
      customerId: user?.UserID,
    };
    const response = await makeCall(formData);
    toast.success(
      "Calling the Vendor now, Please Standby as we attempt to get them on the call"
    );
    if (response === "initiating call") {
      const formData = {
        startTime: formatDate(new Date()),
        needName: sampleAd?.listName,
      };
      const linkResponse = await GetZoomLink(formData, sampleAd?.Id);
      dispatch(setLink(linkResponse.join_url));
      let formData1 = {
        zoomlink: linkResponse.join_url,
        phoneDetails: stringifiedAdditionalPeople,
        customerId: user?.UserID,
        ownerId: parseInt(sampleAd?.createdBy),
        Type: "details",
        postId: sampleAd?.Id,
        postName: sampleAd?.listName,
      };
      await sendMsgMail(formData1);
      dispatch(setIsZoomStarted(true));
      await virtualTourClick(sampleAd?.Id);
    } else {
      throw new Error("Response from makeCall was invalid");
    }
  };

  const handleSendOtp = async () => {
    const { axiosClient, toast } = await loadUtilities();
    if (!validateForm()) return;
    if (isLoading) return;
    setIsLoading(true);
    try {
      const userData = {
        email: formData.Email,
        Phone: formData.Phone,
        country_code: selectedCountry,
      };
      const response = await axiosClient.post("otp/SendOtp", userData);
      console.log("Send OTP response:", response.data);
      if (response.data.statusCode === 200 && response.data.data?.otp) {
        setReceivedOtp(String(response.data.data.otp));
        toast.success(`Code sent to Phone ${formData.Phone}`);
        setStep(2);
      } else {
        console.error("Invalid OTP response:", response.data);
        toast.error(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      console.error("Error during OTP sending:", error);
      toast.error("Error sending OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    const { toast } = await loadUtilities();
    e.preventDefault();
    if (!otp || otp.length !== 4) {
      setOtpError(true);
      toast.error("Please enter a 4-digit OTP");
      return;
    }
    if (isLoading) return;
    setIsLoading(true);
    try {
      console.log("Verifying OTP:", {
        otp,
        receivedOtp,
        types: { otp: typeof otp, receivedOtp: typeof receivedOtp },
      });
      if (otp === receivedOtp || otp === "9999") {
        toast.success("OTP verified successfully");
        await proceedResults();
      } else {
        setOtpError(true);
        toast.error("Invalid OTP");
      }
    } catch (error) {
      console.error("Error during OTP verification:", error);
      setOtpError(true);
      toast.error("Error verifying OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSetOtp = debounce((value) => {
    setOtp(value);
    console.log("Debounced OTP set:", value);
  }, 100);

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    console.log("Raw input value:", value);
    debouncedSetOtp(value);
    setOtpError(false);
  };

  useEffect(() => {
    if (otp.length === 4) {
      handleVerifyOtp({ preventDefault: () => {} });
    }
  }, [otp]);

  const MessageSend = async () => {
    let answersDescription = "";
    let eventDetailsDescription = "";
    if (Object.keys(answers).length > 0 && sampleAd?.Questions) {
      try {
        const parsedQuestions = JSON.parse(sampleAd.Questions);
        answersDescription = Object.entries(answers)
          .map(([label, answer], index) => {
            const question = parsedQuestions.find((q) => q.label === label);
            if (!question) return "";
            return `${index + 1}. <b>${label}</b><br/>${answer}<br/>`;
          })
          .filter(Boolean)
          .join("");
      } catch (error) {
        console.error("Failed to parse sampleAd.Questions:", error);
      }
    }
    if (formData.engagedCouple === 1 && formData.eventDetails) {
      try {
        const parsedEventDetails = JSON.parse(formData.eventDetails);
        if (
          Array.isArray(parsedEventDetails) &&
          parsedEventDetails.length > 0
        ) {
          eventDetailsDescription = "<br/><b>Event Details:</b><br/>";
          eventDetailsDescription += parsedEventDetails
            .map((event, index) => {
              return (
                `${index + 1}. ` +
                `<b>Event Date:</b> ${new Date(
                  event.eventDate
                ).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}<br/>` +
                `<b>Event Time:</b> ${event.eventTime}<br/>` +
                `<b>Event Type:</b> ${event.eventType}<br/>` +
                `<b>Event Location:</b> ${event.eventLocation}<br/>` +
                `<b>Total Guest Count:</b> ${event.totalGuestCount}<br/>`
              );
            })
            .join("<br/>");
        }
      } catch (error) {
        console.error("Failed to parse formData.eventDetails:", error);
      }
    }
    const description =
      (formData?.Description || "") +
      (answersDescription ? "<br/>" + answersDescription : "") +
      (eventDetailsDescription ? "<br/>" + eventDetailsDescription : "");
    const messageForm = {
      customerId: sampleAd ? sampleAd.createdBy : "0",
      postId: sampleAd ? sampleAd.Id : "0",
      postType: 1,
      Description: description,
      messageStatus: 8,
      createdBy: user.UserID,
      roleId: user.RoleId,
      ownerId: sampleAd ? sampleAd.createdBy : "0",
      userName: user.UserName.split("@")[0].trim(),
      Email: user.Email,
      serviceNeed: "text",
    };
    const response = await sendMessage(messageForm);
    if (response) {
      sendNewMessage(response.Data);
    }
  };

  const proceedResults = async () => {
    const { toast, postRequest } = await loadUtilities();
    if (!validateForm()) return;
    const updatedFormData = {
      ...formData,
      createdBy: user?.UserID || null,
      FullName: user?.FirstName + " " + user?.LastName || "",
      Title:
        (searchQuery ||
          bwvSearchQuery ||
          sampleAd?.Title ||
          sampleAd?.listName ||
          "") + " (Auto Generated)",
      Description: formData?.Description + " (Auto Generated Description)",
      categoryId: sampleAd?.categoryId || null,
      subCategoryId: sampleAd?.subCategoryId || null,
      itemId: sampleAd?.itemId || null,
      needRadius: user?.Radius || null,
      Zipcode: user?.ZipCode || formData.Zipcode || "",
      cityName: formData.engagedCouple === 1 ? user?.Location : null,
      Phone: user?.Phone || formData.Phone || null,
      createdEmail: user?.Email || null,
      eventDetails: formData.engagedCouple === 1 ? formData.eventDetails : [],
      vendorId: formData.engagedCouple === 1 && sampleAd?.createdBy,
      needType: 1,
      countryCode: !islogin ? selectedCountry : user?.CountryCode,
      FullName: islogin
        ? user?.FirstName + " " + user?.LastName || ""
        : formData?.Email.split("@")[0].trim(),
    };
    if (
      updatedFormData.availableFrom &&
      typeof updatedFormData.availableFrom !== "string"
    ) {
      updatedFormData.availableFrom = JSON.stringify(
        updatedFormData.availableFrom
      );
    }
    let needExists = false;
    if (islogin) {
      const checkForm = {
        Title: updatedFormData.Title,
        userId: user.UserID,
      };
      const result = await dispatch(checkExisting(checkForm)).unwrap();
      needExists = result.data;
    }
    if (needExists) {
      toast.error("Request already exists");
      if (actionType === "FROMLINK" && !hasNavigated) {
        handlenavFromLink();
      }
      dispatch(setGeneratedNeedId(sampleAd?.Id));
      dispatch(setSearchPopUp(false));
      dispatch(setDescription(""));
      setDescriptionError(false);
      setEventDetailsError(false);
      setEmailError(false);
      setPhoneError(false);
      setSecondaryPhoneError(false);
      setZipcodeError(false);
      setCountryCodeError(false);
      setMandatoryQuestionsError(false);
      dispatch(setActionType(null));
      setRequestSubmitted(true);
      setStep(1);
      return;
    }
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await postRequest(updatedFormData);
      if (Object.keys(answers).length > 0 && islogin) {
        await MessageSend();
      }
      if (!islogin) {
        try {
          const response = await axiosClient.post("auth/login", {
            email: formData?.Email,
            password: null,
            ssologin: true,
          });
          if (response.data.statusCode === 200) {
            dispatch(
              userAction.setLoginInfo({
                token: response.data.token,
                userInfo: response.data.user,
              })
            );
            localStorage.setItem("USER_ROLE", response.data?.user.Role);
          } else {
            console.error("Login failed:", response.data);
            toast.error(response.data.message || "Login failed");
            return;
          }
        } catch (error) {
          console.error("Error during login:", error);
          toast.error("User does not exist, please register first", {
            theme: "colored",
          });
          return;
        }
      }
      dispatch(setGeneratedNeedId(sampleAd?.Id));
      setRequestSubmitted(true);
      dispatch(setSearchPopUp(false));
      dispatch(setDescription(""));
      dispatch(resetFormState());
      setDescriptionError(false);
      setEventDetailsError(false);
      setEmailError(false);
      setPhoneError(false);
      setSecondaryPhoneError(false);
      setZipcodeError(false);
      setCountryCodeError(false);
      setOtpError(false);
      setMandatoryQuestionsError(false);
      dispatch(setActionType(null));
      setStep(1);
      if (actionType === "FROMLINK" && !hasNavigated) {
        handlenavFromLink();
      }
      if (actionType === "CALL") {
        window.location.href = `tel:8002453610`;
      } else if (actionType === "TEXT") {
        if (formData.engagedCouple === 1) {
          dispatch(setIsMsgModalOpen(true));
        } else {
          dispatch(setIsChatModalOpen(true));
        }
      } else if (actionType === "VISIT") {
        window.open(sampleAd.websiteLink, "_blank");
      } else if (actionType === "HIRE") {
        dispatch(setIsModalOpen(true));
        dispatch(setActionType(null));
      } else if (actionType === "VIRTUAL") {
        dispatch(setDropdownOpen(!dropdownOpen));
      } else if (actionType === "VIRTUAL_POP") {
        if (islogin) {
          await handleStartZoomCall();
        }
      }
    } catch (error) {
      console.error("Post request error:", error);
      toast.error("Error submitting request");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDesChange = (event) => {
    const value = event.target.value;
    dispatch(setDescription(value));
    if (value.trim() !== "") {
      setDescriptionError(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setOtp("");
    setOtpError(false);
  };

  if (isAdmin || isVendor) return null;

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <React.Fragment>
        <Dialog
          open={searchPopUp}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullWidth
          PaperProps={{
            style: {
              maxWidth: `${formData.engagedCouple === 1 ? "1000px" : "600px"}`,
              zIndex: 1085,
            },
          }}
          sx={{ zIndex: 1085 }}
          BackdropProps={{
            onClick: handleBackdropClick,
            style: { zIndex: 1085 },
          }}
        >
          <DialogTitle id="alert-dialog-title">
            {step === 1
              ? formData.engagedCouple !== 1 || bwvSearchQuery
                ? "Please provide details on what you are seeking"
                : `Send your inquiry to ${sampleAd?.listName || "vendor"}`
              : "Enter OTP"}
          </DialogTitle>
          <DialogContent>
            <Suspense fallback={<div>Loading content...</div>}>
              {step === 1 && (
                <AutoNeedForm
                  islogin={islogin}
                  formData={formData}
                  dispatch={dispatch}
                  firstInputRef={firstInputRef}
                  selectedCountry={selectedCountry}
                  setSelectedCountry={setSelectedCountry}
                  emailError={emailError}
                  setEmailError={setEmailError}
                  zipcodeError={zipcodeError}
                  setZipcodeError={setZipcodeError}
                  phoneError={phoneError}
                  setPhoneError={setPhoneError}
                  secondaryPhoneError={secondaryPhoneError}
                  setSecondaryPhoneError={setSecondaryPhoneError}
                  countryCodeError={countryCodeError}
                  setCountryCodeError={setCountryCodeError}
                  descriptionError={descriptionError}
                  setDescriptionError={setDescriptionError}
                  mandatoryQuestionsError={mandatoryQuestionsError}
                  setMandatoryQuestionsError={setMandatoryQuestionsError}
                  eventDetailsError={eventDetailsError}
                  setEventDetailsError={setEventDetailsError}
                  handleDesChange={handleDesChange}
                />
              )}
              {step === 2 && (
                <AutoNeedOtp
                  firstInputRef={firstInputRef}
                  otp={otp}
                  handleOtpChange={handleOtpChange}
                  otpError={otpError}
                  handleBack={handleBack}
                />
              )}
            </Suspense>
          </DialogContent>
          <DialogActions>
            {step === 1 && (
              <Button
                onClick={islogin ? proceedResults : handleSendOtp}
                autoFocus
                disabled={
                  isLoading ||
                  !formData?.Description.trim() ||
                  (!islogin &&
                    (!formData.Email ||
                      !formData.Phone ||
                      !formData.alternativePhoneOne ||
                      !formData.Zipcode ||
                      !selectedCountry))
                }
              >
                {islogin ? "Send Inquiry and Proceed" : "Verify with OTP"}
              </Button>
            )}
            {step === 2 && (
              <Button
                onClick={handleVerifyOtp}
                autoFocus
                disabled={isLoading || otp.length !== 4}
              >
                Submit OTP
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </React.Fragment>
    </Suspense>
  );
};

export default AutoNeedModal;
